import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useCommentFetcher } from '@api/comment.api';
import { useAccountStore } from '@stores/account.store';

export const useCommentStore = defineStore('comment', () => {
  const contentId = ref<string | undefined>(undefined);
  const contentTitle = ref<string | undefined>(undefined);

  const isCommentsDialogOpen = ref<boolean>(false);
  const comments = ref<Array<Comment> | undefined>(undefined);

  const isCommentReplyActive = ref<boolean>(false);
  const isReportCommentActive = ref<boolean>(false);
  const commentsDialogScrollPosition = ref<number | undefined>(undefined);

  const replyToId = ref<string | undefined>(undefined);
  const replyToCommentName = ref<string | undefined>(undefined);
  const replyToComment = ref<string | undefined>(undefined);

  const reportToId = ref<string | undefined>(undefined);
  const reportToCommentName = ref<string | undefined>(undefined);
  const reportToComment = ref<string | undefined>(undefined);

  const editToCommentName = ref<string | undefined>(undefined);
  const editToComment = ref<string | undefined>(undefined);

  const commentFetcher = useCommentFetcher();
  const accountStore = useAccountStore();

  const storeReplyData = (replyId: string | null, name: string | null, comment: string | null) => {
    replyToId.value = replyId as string;
    replyToCommentName.value = name as string;
    replyToComment.value = comment as string;
  };

  const storeReportData = (id: string | null, name: string | null, comment: string | null) => {
    if (!id) return;
    reportToId.value = id as string;
    reportToCommentName.value = name as string;
    reportToComment.value = comment as string;
  };

  const storeEditData = (name: string | null, comment: string | null) => {
    editToCommentName.value = name as string;
    editToComment.value = comment as string;
  };

  const clearReportData = () => {
    reportToId.value = undefined;
    isReportCommentActive.value = false;
  };

  const setReportCommentForm = (payload: boolean) => {
    isReportCommentActive.value = payload;
  };

  const storeCommentDialogScrollPosition = (position: number) => {
    if (commentsDialogScrollPosition.value === position) {
      commentsDialogScrollPosition.value = position + 0.1;
      setTimeout(() => {
        commentsDialogScrollPosition.value = position;
      }, 0);
    } else {
      commentsDialogScrollPosition.value = position;
    }
  };

  const clearReplyData = () => {
    replyToId.value = undefined;
    replyToCommentName.value = undefined;
    replyToComment.value = undefined;
  };

  const openCommentDialog = async (isOpen: boolean, id: string, title: string) => {
    contentId.value = id;
    await fetchComments(id);

    isCommentsDialogOpen.value = isOpen;
    contentTitle.value = title;

    const fetched = commentFetcher.fetch.data;
    comments.value = Array.isArray(fetched.value) ? fetched.value : [];
  };

  const closeCommentDialog = () => {
    isCommentsDialogOpen.value = false;
    contentId.value = undefined;
    contentTitle.value = undefined;
    comments.value = [];
    clearReplyData();
    clearReportData();
  };

  const fetchComments = async (contentId?: string, replyToId?: string) => {
    if (!contentId) return;
    await commentFetcher.fetch.fetchComments({ contentId, replyToId }, accountStore.authToken);
  };

  const postComment = async (contentId?: string, comment?: string, replyToId?: string) => {
    if (!contentId || !comment) return;
    await commentFetcher.post.postComment(
      { contentId, comment, replyToId },
      accountStore.authToken,
    );
  };

  const likeComment = async (id?: string) => {
    if (!id) return;
    await commentFetcher.like.likeComment({ commentId: id }, accountStore.authToken);
  };

  const deleteLikeComment = async (id?: string) => {
    if (!id) return;
    await commentFetcher.deleteLike.deleteLikeComment({ commentId: id }, accountStore.authToken);
  };

  const reportComment = async (id?: string, reason?: string) => {
    if (!id || !reason) return;
    await commentFetcher.report.reportComment(
      { commentId: id, reason: reason },
      accountStore.authToken,
    );
  };

  const patchComment = async (commentId?: string, comment?: string) => {
    if (!commentId || comment === undefined) return;
    await commentFetcher.patch.patchComment({ commentId, comment }, accountStore.authToken);
  };

  return {
    contentId,
    contentTitle,

    isCommentReplyActive,
    isReportCommentActive,

    isCommentsDialogOpen,
    comments,
    openCommentDialog,
    closeCommentDialog,

    replyToId,
    replyToCommentName,
    replyToComment,
    storeReplyData,
    clearReplyData,
    setReportCommentForm,

    reportToId,
    reportToCommentName,
    reportToComment,
    storeReportData,
    clearReportData,

    storeEditData,

    commentsDialogScrollPosition,
    storeCommentDialogScrollPosition,

    commentData: commentFetcher.fetch.data,
    commentError: commentFetcher.fetch.error,
    commentIsFetching: commentFetcher.fetch.isFetching,
    fetchComments,

    commentPostData: commentFetcher.post.data,
    commentPostError: commentFetcher.post.error,
    commentPostIsFetching: commentFetcher.post.isFetching,
    postComment,

    commentLikeData: commentFetcher.like.data,
    commentLikeError: commentFetcher.like.error,
    commentLikeIsFetching: commentFetcher.like.isFetching,
    likeComment,

    commentDeleteLikeData: commentFetcher.deleteLike.data,
    commentDeleteLikeError: commentFetcher.deleteLike.error,
    commentDeleteLikeIsFetching: commentFetcher.deleteLike.isFetching,
    deleteLikeComment,

    commentReportData: commentFetcher.report.data,
    commentReportError: commentFetcher.report.error,
    commentReportisFetching: commentFetcher.report.isFetching,
    reportComment,

    commentPatchData: commentFetcher.patch.data,
    commentPatchError: commentFetcher.patch.error,
    commentPatchIsFetching: commentFetcher.patch.isFetching,
    patchComment,
  };
});
